import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import FlightSearchForm from './components/FlightSearchForm';
import FlightOfferCard from './components/FlightOfferCard';
import Icon from '../../components/AppIcon';
import { searchFlights, normalizeOffer } from '../../services/duffelService';

const SORT_OPTIONS = [
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'duration_asc', label: 'Duration: Shortest' },
  { value: 'stops_asc', label: 'Stops: Fewest First' },
];

const FlightSearch = () => {
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [lastSearch, setLastSearch] = useState(null);

  const handleSearch = async (searchParams) => {
    setLoading(true);
    setError(null);
    setOffers([]);
    setHasSearched(true);
    setLastSearch(searchParams);
    setSelectedOffer(null);

    try {
      const offerRequest = await searchFlights(searchParams);
      const rawOffers = offerRequest?.offers || [];
      const normalized = rawOffers?.map(normalizeOffer);
      setOffers(normalized);
    } catch (err) {
      console.error('[FlightSearch] Search error:', err);
      setError(err?.message || 'Failed to search flights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sortedOffers = [...offers]?.sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return parseFloat(a?.totalAmount || 0) - parseFloat(b?.totalAmount || 0);
      case 'price_desc':
        return parseFloat(b?.totalAmount || 0) - parseFloat(a?.totalAmount || 0);
      case 'duration_asc': {
        const aDur = a?.slices?.[0]?.duration || '';
        const bDur = b?.slices?.[0]?.duration || '';
        return aDur?.localeCompare(bDur);
      }
      case 'stops_asc':
        return (a?.slices?.[0]?.stops || 0) - (b?.slices?.[0]?.stops || 0);
      default:
        return 0;
    }
  });

  const handleSelectOffer = (offer) => {
    setSelectedOffer(offer);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSelection = () => {
    setSelectedOffer(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-36 pb-16 px-4 max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
              <Icon name="Plane" size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Flight Search</h1>
              <p className="text-sm text-muted-foreground">Search and compare flights powered by Duffel</p>
            </div>
          </div>
        </div>

        {/* Selected Offer Banner */}
        {selectedOffer && (
          <div className="mb-6 p-4 rounded-2xl border border-success/30 bg-success/5 flex items-center justify-between animate-fade-in">
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Flight selected — {selectedOffer?.airline?.name} · {selectedOffer?.totalCurrency} {parseFloat(selectedOffer?.totalAmount)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedOffer?.slices?.[0]?.origin} → {selectedOffer?.slices?.[selectedOffer?.slices?.length - 1]?.destination}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearSelection}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Change
              </button>
              <button
                className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
              >
                Continue to Book
              </button>
            </div>
          </div>
        )}

        {/* Search Form */}
        <FlightSearchForm onSearch={handleSearch} loading={loading} />

        {/* Results Section */}
        {loading && (
          <div className="mt-10 flex flex-col items-center gap-4 py-16">
            <div className="h-12 w-12 rounded-full border-4 border-border border-t-transparent animate-spin" style={{ borderTopColor: '#F06F6B' }}></div>
            <p className="text-muted-foreground text-sm">Searching for the best flights…</p>
          </div>
        )}

        {!loading && error && (
          <div className="mt-8 p-5 rounded-2xl border border-destructive/30 bg-destructive/5 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">Search failed</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
              {error?.includes('DUFFEL_ACCESS_TOKEN') && (
                <p className="text-xs text-muted-foreground mt-2 bg-muted rounded-lg px-3 py-2">
                  To enable flight search, add your Duffel access token as <code className="font-mono">DUFFEL_ACCESS_TOKEN</code> in your Supabase Edge Function secrets.
                </p>
              )}
            </div>
          </div>
        )}

        {!loading && hasSearched && !error && offers?.length === 0 && (
          <div className="mt-10 flex flex-col items-center gap-3 py-16 text-center">
            <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center">
              <Icon name="SearchX" size={28} className="text-muted-foreground" />
            </div>
            <p className="text-foreground font-medium">No flights found</p>
            <p className="text-sm text-muted-foreground max-w-sm">
              No offers were returned for this route and date. Try adjusting your search criteria or check that the airport codes are correct.
            </p>
          </div>
        )}

        {!loading && offers?.length > 0 && (
          <div className="mt-8">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {offers?.length} flight{offers?.length !== 1 ? 's' : ''} found
                </p>
                {lastSearch && (
                  <p className="text-xs text-muted-foreground">
                    {lastSearch?.origin} → {lastSearch?.destination} · {lastSearch?.departureDate}
                    {lastSearch?.returnDate ? ` → ${lastSearch?.returnDate}` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground">Sort:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="text-xs border border-input rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {SORT_OPTIONS?.map((opt) => (
                    <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Offer Cards */}
            <div className="space-y-4">
              {sortedOffers?.map((offer) => (
                <FlightOfferCard
                  key={offer?.id}
                  offer={offer}
                  onSelect={handleSelectOffer}
                />
              ))}
            </div>

            <p className="text-xs text-muted-foreground text-center mt-6">
              Prices shown are per person total including taxes. Offers expire — select quickly to lock in your fare.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightSearch;
